import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { Tour } from '@/types/tours';

interface TourContextType {
  activeTour: Tour | null;
  isRunning: boolean;
  startTour: (tourId: string) => void;
  stopTour: () => void;
  completeTour: (tourId: string) => Promise<void>;
  skipTour: (tourId: string) => void;
  restartTour: (tourId: string) => void;
  isTourCompleted: (tourId: string) => boolean;
  pageTours: Tour[];
  allTours: Tour[];
  loading: boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTour, setActiveTour] = useState<Tour | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [pageTours, setPageTours] = useState<Tour[]>([]);
  const [allTours, setAllTours] = useState<Tour[]>([]);
  const [completedTours, setCompletedTours] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  // Função helper para aguardar todos os targets estarem no DOM
  const waitForAllTargets = useCallback((steps: Tour['steps'], timeout = 10000): Promise<boolean> => {
    return new Promise((resolve) => {
      const checkTargets = () => {
        const allPresent = steps.every(step => document.querySelector(step.target));
        return allPresent;
      };

      if (checkTargets()) {
        resolve(true);
        return;
      }

      const observer = new MutationObserver(() => {
        if (checkTargets()) {
          observer.disconnect();
          resolve(true);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Timeout de segurança
      setTimeout(() => {
        observer.disconnect();
        resolve(false);
      }, timeout);
    });
  }, []);

  // Carregar tours da página atual e todos os tours
  useEffect(() => {
    const loadPageTours = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Carregar todos os tours ativos
        const { data: allToursData, error: allToursError } = await supabase
          .from('tours')
          .select('*')
          .eq('is_active', true)
          .order('page', { ascending: true });

        if (allToursError) throw allToursError;

        // Filtrar tours da página atual (com suporte a rotas dinâmicas)
        const tours = allToursData?.filter(t => {
          // Match exato (rotas sem parâmetros dinâmicos)
          if (t.page === location.pathname) return true;
          
          // Match com padrão de rota dinâmica (ex: /casos/:id)
          // Converter ":id" → "[^/]+" (qualquer string exceto /)
          const pattern = t.page
            .replace(/:[^/]+/g, '[^/]+')  // :id → [^/]+
            .replace(/\//g, '\\/');        // / → \/ (escape)
          
          const regex = new RegExp(`^${pattern}$`);
          
          if (regex.test(location.pathname)) {
            console.log(`[TourContext] Match encontrado: "${t.page}" → "${location.pathname}"`);
            return true;
          }
          
          return false;
        }) || [];

        console.log('[TourContext] Rota atual:', location.pathname);
        console.log('[TourContext] Tours encontrados:', tours.length, tours.map(t => t.name));

        // Converter tours para o tipo correto
        const typedTours = (tours || []).map(tour => ({
          ...tour,
          steps: tour.steps as unknown as Tour['steps']
        })) as Tour[];

        // Carregar tours completados do usuário
        const { data: completions, error: completionsError } = await supabase
          .from('tour_completions' as any)
          .select('tour_id')
          .eq('user_id', user.id);

        if (completionsError) throw completionsError;

        const completedIds = new Set((completions || []).map((c: any) => c.tour_id) || []);
        setCompletedTours(completedIds);
        setPageTours(typedTours);
        
        // Converter todos os tours para o tipo correto
        const allTypedTours = (allToursData || []).map(tour => ({
          ...tour,
          steps: tour.steps as unknown as Tour['steps']
        })) as Tour[];
        setAllTours(allTypedTours);

        // Auto-start primeiro tour não completado
        const firstIncomplete = tours?.find(t => t.auto_start && !completedIds.has(t.id));
        if (firstIncomplete && !sessionStorage.getItem(`tour-skipped-${firstIncomplete.id}`)) {
          // Aguardar renderização completa antes de iniciar tour
          setTimeout(async () => {
            console.log('🎯 Aguardando renderização dos targets do tour:', firstIncomplete.name);
            
            // Converter steps para o tipo correto
            const tourSteps = firstIncomplete.steps as unknown as Tour['steps'];
            const allReady = await waitForAllTargets(tourSteps);
            
            if (allReady) {
              console.log('✅ Todos os targets prontos, iniciando tour');
              startTour(firstIncomplete.id);
            } else {
              console.warn('⚠️ Alguns targets não foram encontrados após timeout, iniciando tour parcial');
              const missingTargets = tourSteps
                .filter(step => !document.querySelector(step.target))
                .map(step => step.target);
              console.warn('Targets ausentes:', missingTargets);
              startTour(firstIncomplete.id);
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Erro ao carregar tours:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPageTours();
  }, [location.pathname, user]);

  const startTour = useCallback((tourId: string) => {
    const tour = pageTours.find(t => t.id === tourId);
    if (tour) {
      setActiveTour(tour);
      setIsRunning(true);
    }
  }, [pageTours]);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    setActiveTour(null);
  }, []);

  const completeTour = useCallback(async (tourId: string) => {
    if (!user) return;

    try {
      // Usar upsert com onConflict para evitar erro 409
      const { error } = await supabase
        .from('tour_completions' as any)
        .upsert(
          {
            user_id: user.id,
            tour_id: tourId,
            completed_at: new Date().toISOString(),
          },
          {
            onConflict: 'user_id,tour_id' // Especifica a constraint UNIQUE
          }
        );

      if (error) {
        console.error('Erro ao marcar tour como completo:', error);
        return;
      }

      setCompletedTours(prev => new Set([...prev, tourId]));
      stopTour();
    } catch (error) {
      console.error('Erro ao marcar tour como completo:', error);
    }
  }, [user, stopTour]);

  const skipTour = useCallback((tourId: string) => {
    sessionStorage.setItem(`tour-skipped-${tourId}`, 'true');
    stopTour();
  }, [stopTour]);

  const isTourCompleted = useCallback((tourId: string) => {
    return completedTours.has(tourId);
  }, [completedTours]);

  const restartTour = useCallback((tourId: string) => {
    // Remove da lista de completados
    setCompletedTours(prev => {
      const newSet = new Set(prev);
      newSet.delete(tourId);
      return newSet;
    });
    // Remove do sessionStorage
    sessionStorage.removeItem(`tour-skipped-${tourId}`);
    // Inicia o tour
    startTour(tourId);
  }, [startTour]);

  return (
    <TourContext.Provider value={{
      activeTour,
      isRunning,
      startTour,
      stopTour,
      completeTour,
      skipTour,
      restartTour,
      isTourCompleted,
      pageTours,
      allTours,
      loading
    }}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) throw new Error('useTour must be used within TourProvider');
  return context;
};
